/**
 * @file src/seo/seoHelpers.js
 * @description SEO Engine & Helper Utilities for VelaiVendum Tamil Nadu Job Portal.
 * Designed to strictly adhere to Google 2026 SEO guidelines and outperform competitors like Naukri, Indeed, & LinkedIn Jobs.
 * @module seoHelpers
 */

export const SITE_NAME = 'VelaiVendum';
export const DEFAULT_SITE_URL = 'https://www.velaivendum.com';
export const DEFAULT_OG_IMAGE = 'https://www.velaivendum.com/default-og-image.jpg';
export const DEFAULT_LOGO_URL = 'https://res.cloudinary.com/dopyogopf/image/upload/v1692171207/vvlogo/ga9f89jmmvzbyxhixxi2.jpg';

/**
 * Top Tamil Nadu Cities for SEO targeting
 * @type {string[]}
 */
export const TAMIL_NADU_CITIES = [
  'Chennai', 'Coimbatore', 'Madurai', 'Trichy', 'Salem', 'Erode',
  'Tiruppur', 'Hosur', 'Vellore', 'Kanchipuram', 'Tirunelveli',
  'Karur', 'Namakkal', 'Thanjavur', 'Dindigul', 'Sivakasi', 'Nagercoil', 'Cuddalore'
];

/**
 * Target Job Roles & Categories in Tamil Nadu
 * @type {string[]}
 */
export const TARGET_JOB_ROLES = [
  'React Developer', 'NodeJS Developer', 'Java Developer', 'Python Developer',
  'MERN Stack Developer', 'UI Developer', 'Software Engineer', 'Freshers Jobs',
  'Walk-in Jobs', 'Government Jobs', 'Private Jobs', 'IT Jobs', 'Engineering Jobs'
];

/**
 * @typedef {Object} SEOData
 * @property {string} [title] - Custom or dynamic title
 * @property {string} [description] - Custom or dynamic description
 * @property {string} [keywords] - Comma-separated keywords
 * @property {string} [companyName] - Company name
 * @property {string} [jobTitle] - Job title
 * @property {string} [location] - Location/City
 * @property {string} [experience] - Experience level required
 * @property {string|number} [salary] - Salary or package info
 * @property {string} [employmentType] - Employment type e.g., FULL_TIME, PART_TIME
 * @property {string|number} [hiringCount] - Number of openings
 * @property {string} [industry] - Industry category
 * @property {string} [datePosted] - ISO date string
 * @property {string} [validThrough] - ISO expiry date string
 * @property {Array<{question: string, answer: string}>} [faqs] - FAQ items for FAQPage schema
 * @property {Array<{name: string, item: string}>} [breadcrumbs] - Custom breadcrumb trail
 */

/**
 * Generates a high-CTR, search-optimized Page Title for Google 2026 rank scoring.
 *
 * @param {string} pageType - Type of page e.g. 'home', 'job_detail', 'jobs_list', 'company_detail', 'employer'
 * @param {SEOData} [data={}] - Dynamic page data
 * @returns {string} Optimized Title Tag (50 - 60 chars optimal)
 */
export function getTitle(pageType, data = {}) {
  const { jobTitle, companyName, location, industry } = data;
  const city = location || 'Chennai, Tamil Nadu';

  switch (pageType) {
    case 'home':
      return 'Tamil Nadu Jobs 2026 | Vacancies in Chennai, Coimbatore, Madurai | VelaiVendum';

    case 'job_detail':
      if (jobTitle && companyName) {
        return `${jobTitle} Jobs in ${city} | ${companyName} | VelaiVendum`;
      }
      return `${jobTitle || 'Latest Job Openings'} in ${city} | VelaiVendum`;

    case 'jobs_list':
      if (data.filterQuery) {
        return `${data.filterQuery} Jobs in Tamil Nadu (2026) | VelaiVendum`;
      }
      return `Latest Jobs in ${city} | IT, Walk-in & Freshers Vacancies | VelaiVendum`;

    case 'companies':
      return 'Top Companies Hiring in Tamil Nadu (2026) | Employer Directory | VelaiVendum';

    case 'company_detail':
      return `${companyName || 'Company'} Careers & Jobs in ${city} | ${industry || 'Tamil Nadu'} | VelaiVendum`;

    case 'employer_dashboard':
    case 'employer_register':
      return 'Free Job Posting Tamil Nadu | Hire Top Talent & Recruiters | VelaiVendum';

    case 'jobseeker_register':
      return 'Create Free Job Seeker Profile | Apply to Tamil Nadu Jobs | VelaiVendum';

    case 'login':
      return 'Login | VelaiVendum Tamil Nadu Job Portal';

    case 'contact':
      return 'Contact Us | VelaiVendum Job Support Tamil Nadu';

    case 'about':
      return 'About Us | #1 Tamil Nadu Job Portal & Career Network | VelaiVendum';

    case 'privacy':
      return 'Privacy Policy | VelaiVendum Security & Data Protection';

    case 'terms':
      return 'Terms & Conditions | VelaiVendum Employment Portal';

    case 'pricing':
      return 'Employer Pricing & Free Job Posting Plans | VelaiVendum';

    case 'refund':
      return 'Refund & Cancellation Policy | VelaiVendum';

    case 'delivery':
      return 'Service Delivery Policy | VelaiVendum';

    case 'purchase':
      return 'Purchase & Payment Terms | VelaiVendum';

    default:
      return `${data.title || 'Tamil Nadu Job Portal'} | VelaiVendum`;
  }
}

/**
 * Generates compelling meta descriptions optimized for Google CTR (150-160 chars).
 *
 * @param {string} pageType
 * @param {SEOData} [data={}]
 * @returns {string} Meta Description
 */
export function getDescription(pageType, data = {}) {
  const { jobTitle, companyName, location, experience, salary, employmentType, hiringCount, industry } = data;
  const city = location || 'Chennai & Tamil Nadu';

  switch (pageType) {
    case 'home':
      return 'Search 50,000+ Tamil Nadu Jobs on VelaiVendum. Explore Chennai, Coimbatore, Madurai IT, Walk-in, Freshers, Private & Govt job openings. Apply now for free!';

    case 'job_detail': {
      const expText = experience ? `Experience: ${experience}.` : '';
      const salText = salary ? `Salary: ${salary}.` : '';
      const empText = employmentType ? `Type: ${employmentType}.` : '';
      return `Apply for ${jobTitle || 'Job'} at ${companyName || 'Top Employer'} in ${city}. ${expText} ${salText} ${empText} Fast application on VelaiVendum.`;
    }

    case 'jobs_list':
      return `Find active ${data.filterQuery || 'IT, Software, Freshers & Walk-in'} job openings in ${city}. Filter by salary, experience, and role. Apply in 1-click on VelaiVendum!`;

    case 'companies':
      return 'Browse verified employers and companies hiring in Tamil Nadu. View open job vacancies, company profiles, and direct recruiter contacts on VelaiVendum.';

    case 'company_detail': {
      const hiringText = hiringCount ? `Currently hiring ${hiringCount} positions.` : 'Explore current job openings.';
      return `${companyName} is hiring in ${city}! ${hiringText} Industry: ${industry || 'Services'}. Apply directly to company vacancies on VelaiVendum.`;
    }

    case 'employer_dashboard':
    case 'employer_register':
      return 'Post jobs for free on VelaiVendum Tamil Nadu. Access top talent, candidate database, recruitment software, and hire fast across Chennai, Coimbatore & Madurai.';

    case 'jobseeker_register':
      return 'Register your job seeker profile for free on VelaiVendum. Receive instant job alerts, recommendations, and get hired by top recruiters in Tamil Nadu.';

    case 'login':
      return 'Login to your VelaiVendum account to manage job applications, update your resume, or post new job openings in Tamil Nadu.';

    case 'contact':
      return 'Need help or support with job listings or candidate hiring? Contact VelaiVendum customer service and partner support team today.';

    case 'about':
      return 'VelaiVendum is Tamil Nadu\'s premier job portal connecting top employers with skilled candidates in Chennai, Coimbatore, Madurai, and beyond.';

    case 'privacy':
      return 'Read VelaiVendum privacy policy to understand how we secure your personal data, job application details, and profile security in Tamil Nadu.';

    case 'terms':
      return 'VelaiVendum terms of service outlining conditions for job seekers, employer job postings, database access, and platform rules.';

    case 'pricing':
      return 'Explore affordable job posting packages, resume database access plans, and recruiter subscription rates on VelaiVendum.';

    case 'refund':
      return 'Review VelaiVendum refund and cancellation terms for employer packages, subscriptions, and database services.';

    case 'delivery':
      return 'Learn how digital job posting services, database access, and candidate alert services are delivered on VelaiVendum.';

    case 'purchase':
      return 'Read VelaiVendum purchase policy and transaction terms for employers and premium job placement services.';

    default:
      return data.description || 'VelaiVendum is Tamil Nadu\'s leading job portal for freshers, experienced professionals, and top employers.';
  }
}

/**
 * Generates highly targeted meta keywords for search engines.
 *
 * @param {string} pageType
 * @param {SEOData} [data={}]
 * @returns {string} Comma-separated keyword list
 */
export function getKeywords(pageType, data = {}) {
  const baseKeywords = [
    'Tamil Nadu Jobs', 'Jobs in Chennai', 'Latest Jobs Tamil Nadu',
    'Walk-in Jobs Chennai', 'Freshers Jobs', 'Private Jobs', 'Government Jobs',
    'Software Jobs', 'Engineering Jobs', 'IT Jobs Chennai', 'Developer Jobs Chennai',
    'React Developer Jobs', 'NodeJS Jobs', 'Java Jobs', 'Python Jobs', 'MERN Jobs', 'UI Developer Jobs',
    'Coimbatore Jobs', 'Madurai Jobs', 'Trichy Jobs', 'Salem Jobs', 'Erode Jobs', 'Hosur Jobs'
  ];

  if (pageType === 'job_detail' && data.jobTitle) {
    return [
      data.jobTitle,
      `${data.jobTitle} in ${data.location || 'Chennai'}`,
      `${data.companyName} Careers`,
      `${data.jobTitle} Vacancy Tamil Nadu`,
      ...baseKeywords.slice(0, 8)
    ].join(', ');
  }

  if (pageType === 'company_detail' && data.companyName) {
    return [
      `${data.companyName} Jobs`,
      `${data.companyName} Vacancies`,
      `${data.companyName} Careers ${data.location || 'Chennai'}`,
      'Free Job Posting', 'Hire Employees Tamil Nadu',
      ...baseKeywords.slice(0, 8)
    ].join(', ');
  }

  return baseKeywords.join(', ');
}

/**
 * Generates a clean, normalized Canonical URL without tracking or session parameters.
 *
 * @param {string} pathname - Current route path
 * @param {string} [origin=DEFAULT_SITE_URL] - Base site URL origin
 * @param {Object} [queryParams={}] - Query params object
 * @returns {string} Normalized absolute canonical URL
 */
export function getCanonical(pathname = '/', origin = DEFAULT_SITE_URL, queryParams = {}) {
  const cleanPath = pathname.split('?')[0].split('#')[0];
  const url = new URL(cleanPath === '/' ? '' : cleanPath, origin);

  // Preserve essential pagination or primary filter if clean
  if (queryParams.page && Number(queryParams.page) > 1) {
    url.searchParams.set('page', String(queryParams.page));
  }

  return url.toString();
}

/**
 * Builds Google 2026 compliant JSON-LD Structured Data Objects.
 * Prevents duplications and handles Organization, WebSite, JobPosting, BreadcrumbList, FAQPage, WebPage, & CollectionPage.
 *
 * @param {string} pageType
 * @param {SEOData} [data={}]
 * @param {string} canonicalUrl
 * @returns {Object[]} Array of JSON-LD Schema objects
 */
export function getSchema(pageType, data = {}, canonicalUrl = DEFAULT_SITE_URL) {
  const schemas = [];

  // 1. Organization Schema (Included everywhere as Publisher)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${DEFAULT_SITE_URL}/#organization`,
    'name': SITE_NAME,
    'url': DEFAULT_SITE_URL,
    'logo': {
      '@type': 'ImageObject',
      'url': DEFAULT_LOGO_URL,
      'caption': 'VelaiVendum Logo'
    },
    'sameAs': [
      'https://www.facebook.com/VelaiVendum',
      'https://twitter.com/VelaiVendum',
      'https://www.linkedin.com/company/VelaiVendum'
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'telephone': '+91-9043267840',
      'contactType': 'customer support',
      'areaServed': 'IN',
      'availableLanguage': ['English', 'Tamil']
    }
  };
  schemas.push(organizationSchema);

  // 2. WebSite Schema with Sitelinks SearchBox (For Home & List pages)
  if (pageType === 'home' || pageType === 'jobs_list') {
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${DEFAULT_SITE_URL}/#website`,
      'url': DEFAULT_SITE_URL,
      'name': SITE_NAME,
      'description': 'Tamil Nadu Job Portal for IT, Freshers, Walk-in & Government Jobs',
      'publisher': { '@id': `${DEFAULT_SITE_URL}/#organization` },
      'potentialAction': {
        '@type': 'SearchAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': `${DEFAULT_SITE_URL}/searchpage?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    };
    schemas.push(websiteSchema);
  }

  // 3. JobPosting Schema (For Job Detail Page)
  if (pageType === 'job_detail') {
    const { jobTitle, companyName, description, location, datePosted, validThrough, employmentType, salary } = data;
    const jobSchema = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      'title': jobTitle || 'Job Opening',
      'description': description || `${jobTitle} at ${companyName} in ${location || 'Tamil Nadu'}.`,
      'identifier': {
        '@type': 'PropertyValue',
        'name': companyName || SITE_NAME,
        'value': data.id || String(Date.now())
      },
      'datePosted': datePosted || new Date().toISOString(),
      'validThrough': validThrough || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      'employmentType': (employmentType || 'FULL_TIME').toUpperCase().replace('-', '_'),
      'hiringOrganization': {
        '@type': 'Organization',
        'name': companyName || 'Verified Employer',
        'sameAs': DEFAULT_SITE_URL,
        'logo': DEFAULT_LOGO_URL
      },
      'jobLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': location || 'Chennai',
          'addressRegion': 'Tamil Nadu',
          'addressCountry': 'IN'
        }
      },
      'applicantLocationRequirements': {
        '@type': 'Country',
        'name': 'India'
      }
    };

    if (salary) {
      jobSchema.baseSalary = {
        '@type': 'MonetaryAmount',
        'currency': 'INR',
        'value': {
          '@type': 'QuantitativeValue',
          'value': typeof salary === 'number' ? salary : 25000,
          'unitText': 'MONTH'
        }
      };
    }

    schemas.push(jobSchema);
  }

  // 4. CollectionPage Schema (For Jobs List & Companies Directory)
  if (pageType === 'jobs_list' || pageType === 'companies') {
    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'url': canonicalUrl,
      'name': getTitle(pageType, data),
      'description': getDescription(pageType, data),
      'isPartOf': { '@id': `${DEFAULT_SITE_URL}/#website` }
    };
    schemas.push(collectionSchema);
  }

  // 5. BreadcrumbList Schema
  const breadcrumbItems = [
    { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': DEFAULT_SITE_URL }
  ];

  if (pageType === 'job_detail') {
    breadcrumbItems.push(
      { '@type': 'ListItem', 'position': 2, 'name': 'Jobs', 'item': `${DEFAULT_SITE_URL}/searchpage` },
      { '@type': 'ListItem', 'position': 3, 'name': data.jobTitle || 'Job Detail', 'item': canonicalUrl }
    );
  } else if (pageType === 'company_detail') {
    breadcrumbItems.push(
      { '@type': 'ListItem', 'position': 2, 'name': 'Companies', 'item': `${DEFAULT_SITE_URL}/companies` },
      { '@type': 'ListItem', 'position': 3, 'name': data.companyName || 'Company', 'item': canonicalUrl }
    );
  } else if (pageType !== 'home') {
    breadcrumbItems.push(
      { '@type': 'ListItem', 'position': 2, 'name': pageType.replace('_', ' ').toUpperCase(), 'item': canonicalUrl }
    );
  }

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbItems
  });

  // 6. FAQPage Schema (If data includes faqs array)
  if (data.faqs && Array.isArray(data.faqs) && data.faqs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': data.faqs.map((faq) => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer
        }
      }))
    });
  }

  return schemas;
}
